// Home / Dashboard Page

import { query } from '../db.js';
import { navigateTo } from '../router.js';

export async function renderHomePage() {
  const app = document.getElementById('app');

  // Get greeting based on time
  const hour = new Date().getHours();
  let greeting = 'Good Evening';
  if (hour < 12) greeting = 'Good Morning';
  else if (hour < 17) greeting = 'Good Afternoon';

  // Get user settings
  const settings = window.appState.settings;

  // Get workout data
  const stats = getWorkoutStats();
  const workoutMap = getWorkoutDates();
  const frequency = calculateWorkoutFrequency();
  const weekdayData = calculateWeekdayDistribution();

  // Get last workout
  const recentWorkouts = query(`
    SELECT wi.*, wt.name as template_name
    FROM PerformedWorkout wi
    JOIN Template wt ON wi.template_id = wt.id
    ORDER BY wi.date DESC, wi.id DESC
    LIMIT 1
  `);

  const lastWorkout = recentWorkouts[0];
  const daysSince = lastWorkout ? getDaysSince(lastWorkout.date) : 0;

  app.innerHTML = `
    <div class="page safe-top">
      <!-- Header -->
      <header style="padding: var(--spacing-lg) var(--spacing-md);">
        <p class="text-muted text-sm" style="text-transform: uppercase; letter-spacing: 2px;">
          ${greeting}
        </p>
        <h1 class="font-display" style="font-size: 2rem; margin-top: 0.5rem;">
          Ready to Train${settings.name !== 'User' ? ', ' + settings.name : ''}?
        </h1>
      </header>

      <!-- Quick Start -->
      <section style="padding: 0 var(--spacing-md); margin-bottom: var(--spacing-lg);">
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md);">
          <button class="btn-glow" id="start-workout-btn" style="display: flex; flex-direction: column; align-items: center; padding: var(--spacing-lg);">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="3"></circle>
              <line x1="3" y1="12" x2="9" y2="12"></line>
              <line x1="15" y1="12" x2="21" y2="12"></line>
            </svg>
            <span style="margin-top: var(--spacing-xs);">Strength</span>
          </button>
          <button class="btn-secondary" id="cardio-entry-btn" style="display: flex; flex-direction: column; align-items: center; padding: var(--spacing-lg); background: linear-gradient(135deg, #FF9500 0%, #FFB647 100%); color: white; border: none; border-radius: var(--radius-lg); cursor: pointer; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
            <span style="margin-top: var(--spacing-xs);">Cardio</span>
          </button>
        </div>
      </section>

      <!-- Quick Stats -->
      <section style="padding: 0 var(--spacing-md); margin-bottom: var(--spacing-lg);">
        <div class="quick-stats">
          <div class="stat-card">
            <span class="stat-value">${stats.currentStreak}</span>
            <span class="stat-label">Day Streak</span>
          </div>
          <div class="stat-card">
            <span class="stat-value">${stats.avgWorkoutsPerWeek}</span>
            <span class="stat-label">Per Week</span>
          </div>
          <div class="stat-card">
            <span class="stat-value">${stats.avgWorkoutsPerMonth}</span>
            <span class="stat-label">Per Month</span>
          </div>
        </div>
      </section>

      <!-- Consistency Calendar -->
      <section style="padding: 0 var(--spacing-md); margin-bottom: var(--spacing-lg);">
        <h2 class="font-display" style="font-size: 1.125rem; margin-bottom: var(--spacing-md);">
          Consistency
        </h2>
        ${renderConsistencyCalendar(workoutMap)}
      </section>

      <!-- Frequency Metrics -->
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--spacing-md); padding: 0 var(--spacing-md); margin-bottom: var(--spacing-lg);">
        <div style="text-align: center; padding: var(--spacing-md); background: var(--color-card); border: 1px solid var(--color-border); border-radius: var(--radius-lg);">
          <div class="stat-value">${frequency.perWeek}</div>
          <div class="text-xs text-muted">/ week</div>
        </div>
        <div style="text-align: center; padding: var(--spacing-md); background: var(--color-card); border: 1px solid var(--color-border); border-radius: var(--radius-lg);">
          <div class="stat-value">${frequency.perMonth}</div>
          <div class="text-xs text-muted">/ month</div>
        </div>
        <div style="text-align: center; padding: var(--spacing-md); background: var(--color-card); border: 1px solid var(--color-border); border-radius: var(--radius-lg);">
          <div class="stat-value">${frequency.perYear}</div>
          <div class="text-xs text-muted">/ year</div>
        </div>
      </div>

      <!-- Last Workout -->
      ${lastWorkout ? `
        <section style="padding: 0 var(--spacing-md); margin-bottom: var(--spacing-lg);">
          <div class="flex items-center justify-between mb-4">
            <h2 class="font-display" style="font-size: 1.125rem;">Last Workout</h2>
            <span class="text-sm text-muted">
              ${daysSince === 0 ? 'Today' : daysSince === 1 ? 'Yesterday' : `${daysSince} days ago`}
            </span>
          </div>
          <div class="card">
            <h3 class="font-display" style="font-size: 1.25rem; margin-bottom: 0.5rem;">
              ${lastWorkout.template_name}
            </h3>
            <p class="text-sm text-muted">
              ${formatTime(lastWorkout.start_time)} - ${formatTime(lastWorkout.end_time)}
              ${lastWorkout.studio ? ` · ${lastWorkout.studio}` : ''}
            </p>
          </div>
        </section>
      ` : ''}

      <!-- Weekday Distribution -->
      ${weekdayData.length > 0 ? `
        <section style="padding: 0 var(--spacing-md); margin-bottom: var(--spacing-xl);">
          <h2 class="font-display" style="font-size: 1.125rem; margin-bottom: var(--spacing-md);">
            Weekday Distribution
          </h2>
          <div style="display: flex; justify-content: space-between; align-items: flex-end; height: 200px; gap: var(--spacing-xs);">
            ${weekdayData.map(day => `
              <div style="flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%;">
                <div style="width: 100%; background: linear-gradient(to top, #FF4444, #FF6B6B); border-radius: var(--radius-md) var(--radius-md) 0 0; height: ${day.percentage}%;"></div>
                <div class="text-xs text-muted" style="margin-top: var(--spacing-xs);">${day.percentage}%</div>
                <div class="text-xs text-muted">${day.day}</div>
              </div>
            `).join('')}
          </div>
        </section>
      ` : ''}
    </div>
  `;

  // Event listeners
  document.getElementById('start-workout-btn')?.addEventListener('click', () => {
    navigateTo('/templates');
  });

  document.getElementById('cardio-entry-btn')?.addEventListener('click', () => {
    navigateTo('/cardio-entry');
  });
}

function getWorkoutStats() {
  // Calculate current streak
  let currentStreak = 0;
  const allWorkouts = query(`
    SELECT DISTINCT date
    FROM PerformedWorkout
    ORDER BY date DESC
  `);

  if (allWorkouts.length > 0) {
    let currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    for (const workout of allWorkouts) {
      const workoutDate = new Date(workout.date);
      workoutDate.setHours(0, 0, 0, 0);

      const diffDays = Math.floor((currentDate - workoutDate) / (1000 * 60 * 60 * 24));

      if (diffDays <= currentStreak + 1) {
        currentStreak++;
        currentDate = workoutDate;
      } else {
        break;
      }
    }
  }

  // Calculate frequency
  const frequency = calculateWorkoutFrequency();

  return {
    currentStreak,
    avgWorkoutsPerWeek: frequency.perWeek,
    avgWorkoutsPerMonth: frequency.perMonth
  };
}

function getWorkoutDates() {
  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
  const startDate = twelveMonthsAgo.toISOString().split('T')[0];

  const workouts = query(`
    SELECT date, COUNT(*) as count
    FROM PerformedWorkout
    WHERE date >= ?
    GROUP BY date
    ORDER BY date ASC
  `, [startDate]);

  // Create map of date -> count
  const workoutMap = {};
  workouts.forEach(w => {
    workoutMap[w.date] = w.count;
  });

  return workoutMap;
}

function calculateWorkoutFrequency() {
  const allWorkouts = query(`SELECT date FROM PerformedWorkout ORDER BY date ASC`);

  if (allWorkouts.length === 0) {
    return { perWeek: 0, perMonth: 0, perYear: 0 };
  }

  const firstDate = new Date(allWorkouts[0].date);
  const today = new Date();
  const daysSinceFirst = Math.max(1, Math.floor((today - firstDate) / (1000 * 60 * 60 * 24)));

  const totalWorkouts = allWorkouts.length;

  return {
    perWeek: Math.round(totalWorkouts / (daysSinceFirst / 7)),
    perMonth: Math.round(totalWorkouts / (daysSinceFirst / 30.44)),
    perYear: Math.round(totalWorkouts / (daysSinceFirst / 365.25))
  };
}

function calculateWeekdayDistribution() {
  const allWorkouts = query(`SELECT date FROM PerformedWorkout`);

  if (allWorkouts.length === 0) {
    return [];
  }

  // Count workouts by day of week
  const dayCounts = [0, 0, 0, 0, 0, 0, 0]; // Sun-Sat
  allWorkouts.forEach(w => {
    const date = new Date(w.date);
    const dayIndex = date.getDay(); // 0=Sun, 6=Sat
    dayCounts[dayIndex]++;
  });

  const total = allWorkouts.length;

  // Reorder to start with Monday
  const reordered = [
    { day: 'Mon', percentage: ((dayCounts[1] / total) * 100).toFixed(1) },
    { day: 'Tue', percentage: ((dayCounts[2] / total) * 100).toFixed(1) },
    { day: 'Wed', percentage: ((dayCounts[3] / total) * 100).toFixed(1) },
    { day: 'Thu', percentage: ((dayCounts[4] / total) * 100).toFixed(1) },
    { day: 'Fri', percentage: ((dayCounts[5] / total) * 100).toFixed(1) },
    { day: 'Sat', percentage: ((dayCounts[6] / total) * 100).toFixed(1) },
    { day: 'Sun', percentage: ((dayCounts[0] / total) * 100).toFixed(1) }
  ];

  return reordered;
}

function renderConsistencyCalendar(workoutMap) {
  const today = new Date();
  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);

  // Start from the first Monday before or on the 12-month-ago date
  const startDate = new Date(twelveMonthsAgo);
  const dayOfWeek = startDate.getDay();
  const daysToMonday = (dayOfWeek === 0 ? 6 : dayOfWeek - 1);
  startDate.setDate(startDate.getDate() - daysToMonday);

  // Calculate total weeks needed
  const daysDiff = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
  const totalWeeks = Math.ceil(daysDiff / 7);

  // Generate week columns
  let weeksHTML = '';
  const currentDate = new Date(startDate);

  for (let week = 0; week < totalWeeks; week++) {
    weeksHTML += '<div style="display: grid; grid-template-rows: repeat(7, 12px); gap: 3px;">';

    for (let day = 0; day < 7; day++) {
      const dateStr = currentDate.toISOString().split('T')[0];
      const hasWorkout = workoutMap[dateStr] > 0;
      const isFuture = currentDate > today;

      const cellStyle = isFuture
        ? 'background: transparent;'
        : hasWorkout
          ? 'background: var(--color-primary); border: 1px solid var(--color-primary);'
          : 'background: var(--color-surface); border: 1px solid var(--color-border);';

      weeksHTML += `<div style="width: 12px; height: 12px; border-radius: 2px; ${cellStyle}"></div>`;
      currentDate.setDate(currentDate.getDate() + 1);
    }

    weeksHTML += '</div>';
  }

  const monthLabels = generateMonthLabels();
  const labelColumnWidth = 40; // Width for day labels column

  return `
    <div style="overflow-x: auto; -webkit-overflow-scrolling: touch;">
      <div style="display: inline-block; min-width: 100%;">
        <!-- Calendar Grid -->
        <div style="display: grid; grid-template-columns: ${labelColumnWidth}px repeat(${totalWeeks}, 12px); gap: 3px;">
          <!-- Day labels column -->
          <div style="display: grid; grid-template-rows: repeat(7, 12px); gap: 3px;">
            <div class="text-xs text-muted" style="font-size: 10px; line-height: 12px;">Mon</div>
            <div></div>
            <div class="text-xs text-muted" style="font-size: 10px; line-height: 12px;">Wed</div>
            <div></div>
            <div class="text-xs text-muted" style="font-size: 10px; line-height: 12px;">Fri</div>
            <div></div>
            <div class="text-xs text-muted" style="font-size: 10px; line-height: 12px;">Sun</div>
          </div>

          <!-- Week columns -->
          ${weeksHTML}
        </div>

        <!-- Month labels -->
        <div style="display: grid; grid-template-columns: ${labelColumnWidth}px repeat(${totalWeeks}, 12px); gap: 3px; margin-top: 8px;">
          <div></div> <!-- Empty space for day labels column -->
          ${monthLabels}
        </div>
      </div>
    </div>
  `;
}

function generateMonthLabels() {
  const today = new Date();
  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);

  // Start from the first Monday before or on the 12-month-ago date
  const startDate = new Date(twelveMonthsAgo);
  const dayOfWeek = startDate.getDay();
  const daysToMonday = (dayOfWeek === 0 ? 6 : dayOfWeek - 1);
  startDate.setDate(startDate.getDate() - daysToMonday);

  // Calculate total weeks
  const daysDiff = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
  const totalWeeks = Math.ceil(daysDiff / 7);

  // Generate labels for each week
  const labels = [];
  const currentDate = new Date(startDate);
  let lastMonth = -1;

  for (let week = 0; week < totalWeeks; week++) {
    const weekMonth = currentDate.getMonth();
    const weekYear = currentDate.getFullYear();

    // Show month label only at the start of a new month
    if (weekMonth !== lastMonth || week === 0) {
      const monthName = currentDate.toLocaleDateString('en-US', { month: 'short' });
      labels.push(`<div class="text-xs text-muted" style="font-size: 10px; text-align: center;">${monthName}</div>`);
      lastMonth = weekMonth;
    } else {
      labels.push(`<div></div>`); // Empty cell
    }

    currentDate.setDate(currentDate.getDate() + 7);
  }

  return labels.join('');
}

function getDaysSince(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

function formatTime(timeStr) {
  if (!timeStr) return 'N/A';
  const [hours, minutes] = timeStr.split(':');
  return `${hours}:${minutes}`;
}
